// 🧭 D&G investigations (v128.5) — the admin-app case-file UI (Phase 2 of
// docs/policies-dg-plan.md; decisions banked with Matt 29 Jul).
//
// Renders into #dgCasesRoot (People → 🧭 D&G sub-tab). Admin app ONLY —
// dg_cases never appears in the staff app or on /api/sync. All reads/writes
// go through the Bearer (any admin-tab access can conduct; the open flow
// ASKS who is conducting and records it — Matt's decision).
//
// JACK advises, never decides: the guidance box and letter drafts ride
// POST /admin/dg/cases/:id/{advise,letter}; every exchange is SAVED onto the
// case trail server-side (an investigation record, deliberately not
// ephemeral). Letters download as .txt files — HR signs in real life.
//
// Bare-name globals (DB, staffData, currentAdmin, escapeHtml…) typeof-guarded
// — the classic-script bundle contract.

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

function fmtWhen(iso) {
  try {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ' ' +
      String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  } catch (e) { return ''; }
}

var STAGES = ['fact-finding', 'invite-issued', 'hearing', 'outcome', 'appeal'];

// v130.3 — the case's journey through the organisation (workflow section 20),
// alongside the hearing stages. Keys mirror the Worker's PIPELINE whitelist.
var PIPELINE = [
  ['draft', 'Draft'], ['submitted', 'Submitted'], ['om-preliminary-review', 'OM Preliminary Review'],
  ['hr-investigation', 'HR Investigation'], ['hearing-required', 'Hearing Required'],
  ['investigation-completed', 'Investigation Completed'], ['gm-hr-final-review', 'GM / HR Final Review'],
  ['final-decision', 'Final Decision'], ['employee-notified', 'Employee Notified'],
  ['appeal', 'Appeal'], ['closed', 'Closed'],
];
function pipelineLabel(key) {
  for (var i = 0; i < PIPELINE.length; i++) if (PIPELINE[i][0] === key) return PIPELINE[i][1];
  return key || '';
}

// Dubai is UTC+4 — "today" must come from the house helper, never a naive
// UTC slice (the 00:00–04:00 wrong-day window). Typeof-guarded classic-script
// global; the fallback shifts to Dubai first (the approved worker form).
function todayLocal() {
  if (typeof localDateStr === 'function') return localDateStr(new Date());
  var dubaiNow = new Date(Date.now() + 4 * 3600 * 1000);
  return dubaiNow.toISOString().slice(0, 10);
}

var _cases = [];
var _open = null;        // the full case row when a case is open
var _error = null;
var _busy = false;
var _newOpen = false;    // the start-investigation form is showing
var _categories = null;  // v130.2 — loaded once from /admin/dg/categories

// v130.2 — the HR-amendable category dropdown. Loaded lazily; the Worker
// falls back to its built-in list on a pre-v199-0 DB, so this never blocks.
async function loadCategories() {
  if (_categories) return _categories;
  try {
    var res = await api('/admin/dg/categories');
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && Array.isArray(data.categories)) {
      _categories = data.categories.filter(function (c) { return c.active !== false; });
    }
  } catch (e) { /* fall through */ }
  if (!_categories) _categories = [];
  return _categories;
}

function root() { return document.getElementById('dgCasesRoot'); }

async function load() {
  _error = null;
  try {
    var res = await api('/admin/dg/cases');
    var data = await res.json().catch(function () { return null; });
    if (!res.ok || !data || data.ok === false) {
      _error = (data && data.error) || ('Could not load cases (HTTP ' + res.status + ')');
      _cases = [];
    } else _cases = data.cases || [];
  } catch (e) { _error = 'Could not load cases — check your connection.'; }
  paint();
}

async function openCase(id) {
  try {
    loadCategories().then(function () { if (_open) paint(); });   // v130.2 — the category select
    var res = await api('/admin/dg/cases/' + id);
    var data = await res.json().catch(function () { return null; });
    // v132.30: the case names me. The server will not open it until a reason
    // is recorded, so ask for one rather than showing a dead 403.
    if (res.status === 403 && data && data.break_glass_required) {
      await offerBreakGlass(id, data.error || '');
      return;
    }
    if (res.ok && data && data.case) {
      _open = data.case; _roles = data.roles || null; _guard = data.guard || null;
      _evidence = null; _audit = null; paint(); loadEvidence(id);
      loadAccounts().then(function () { if (_open) paint(); });   // v130.5 — the exclusion picker
      loadAudit(id);   // v132.29: the append-only history, shown on the case itself
    }
  } catch (e) { /* stay on the list */ }
}

// -- The four roles and the conflict guard (v132.30, migration v214-0) -------
// Who is the subject, who investigates, who decides, who hears an appeal. The
// server refuses to let one person hold two of them, so this panel makes the
// roles VISIBLE: a guard nobody can see is a guard nobody trusts.
var _roles = null;
var _guard = null;

var ROLE_HELP = {
  subject: "The person the case is about. They cannot open this case.",
  investigator: "Gathers the facts.",
  decider: "Decides the outcome.",
  appeal: "Hears an appeal against that decision. Must not be the decision maker.",
};

function roleSelect(r) {
  var opts = ["<option value=''>not set</option>"].concat((_accounts || []).map(function (a) {
    return "<option translate='no' value='" + a.id + "'" + (String(a.id) === String(r.account_id) ? " selected" : "") + ">" +
      esc(a.name || ("account #" + a.id)) + "</option>";
  }));
  return "<select class='dgRole' data-role='" + esc(r.key) + "' style='padding:5px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12px;min-width:150px'>" +
    opts.join("") + "</select>";
}

function rolesPanelHtml() {
  if (!_roles) return "";
  var unchecked = (_guard && _guard.unchecked) || [];
  var rows = _roles.map(function (r) {
    var named = r.who
      ? (r.checked
          ? "<b translate='no'>" + esc(r.who) + "</b>"
          : "<span translate='no'>" + esc(r.who) + "</span> <span style='color:var(--ui-warn)'>(name only)</span>")
      : "<span style='color:var(--sub)'>nobody yet</span>";
    return "<tr>" +
      "<td style='padding:4px 10px 4px 0;font-size:12px;white-space:nowrap'><b>" + esc(r.label) + "</b></td>" +
      "<td style='padding:4px 10px 4px 0;font-size:12px'>" + named + "</td>" +
      "<td style='padding:4px 0'>" + roleSelect(r) + "</td>" +
      "<td style='padding:4px 0 4px 10px;font-size:11px;color:var(--sub)'>" + esc(ROLE_HELP[r.key] || "") + "</td>" +
    "</tr>";
  }).join("");
  var note = "";
  if (_guard && _guard.migrated === false) {
    note = "<div style='background:var(--ap-orange-bg);border:1px solid var(--ap-orange-line);border-radius:8px;padding:8px 12px;font-size:12px;color:var(--ap-orange-deep);margin-bottom:8px'>" +
      "The conflict guard is not switched on yet. Run <b>migrations/v214-0-dg-conflict.sql</b>. " +
      "Until then these roles cannot be saved and nothing is checked.</div>";
  } else if (unchecked.length) {
    note = "<div style='font-size:11.5px;color:var(--ap-orange-deep);margin-bottom:8px'>" + unchecked.length +
      " role(s) are written in by hand rather than picked from the account list, so the guard cannot check them. " +
      "Pick the person to have them checked.</div>";
  }
  return "<details style='margin-top:8px'" + ((_guard && _guard.active) ? "" : " open") + ">" +
    "<summary style='font-size:12.5px;font-weight:700;cursor:pointer'>👥 Who is who on this case" +
      ((_guard && _guard.active) ? " · guard on" : " · guard not checking") + "</summary>" +
    "<div style='margin-top:8px'>" + note +
      "<table style='border-collapse:collapse'>" + rows + "</table>" +
      "<div style='font-size:11.5px;color:var(--sub);margin-top:8px'>One person cannot hold two of these roles. " +
      "The save is refused if they would, whoever you are.</div>" +
      "<button onclick='DgCases.saveRoles()' style='margin-top:8px;background:var(--brand);color:#fff;border:none;border-radius:8px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer'>Save roles</button>" +
    "</div>" +
  "</details>";
}

async function saveRoles() {
  if (!_open) return;
  var patch = {};
  var sels = document.querySelectorAll(".dgRole");
  for (var i = 0; i < sels.length; i++) {
    var key = sels[i].getAttribute("data-role");
    var col = key === "decider" ? "decider_account_id" : (key + "_account_id");
    patch[col] = sels[i].value === "" ? null : parseInt(sels[i].value, 10);
  }
  var ok = await patchCase(patch);
  if (ok) openCase(_open.id);
}

// The one way past the subject rule. The reason is typed, recorded on the
// append-only history for good, and pushed to the other Head Office
// accounts. It is deliberately not a checkbox.
async function offerBreakGlass(id, why) {
  var reason = window.prompt((why || "This case names you.") +
    "\n\nType why you need to open it. This is recorded on the case permanently and the other Head Office accounts are told.");
  if (reason === null) return;
  try {
    var res = await api("/admin/dg/cases/" + id + "/break-glass", {
      method: "POST", body: JSON.stringify({ reason: reason }),
    });
    var data = await res.json().catch(function () { return null; });
    if (!res.ok) { toastSafe((data && data.error) || "The override was refused.", "error"); return; }
    toastSafe("Recorded. The other Head Office accounts have been told.", "success");
    openCase(id);
  } catch (e) { toastSafe("Could not record the override.", "error"); }
}
// ── The audit trail (v132.29, migration v213-0) ───────────────────────────────
// Matt: "if a dismissal is challenged, an editable record is worth very little
// as evidence." This panel is the point of that work: the history is ON the
// case, so a manager can read who changed what, from what to what, without
// anyone querying the database for them.
//
// null = loading, false = the table is not there yet (say so, do not pretend
// there is no history), [] = nothing recorded yet.
var _audit = null;

async function loadAudit(caseId) {
  try {
    var res = await api('/admin/dg/cases/' + caseId + '/audit');
    var data = await res.json().catch(function () { return null; });
    _audit = (res.ok && data && Array.isArray(data.audit)) ? data.audit : false;
  } catch (e) { _audit = false; }
  if (_open) paint();
}

var AUDIT_ICON = {
  open: '📂', update: '✏️', access: '🔒', note: '📝', advice: '🤖',
  letter: '✉️', evidence: '📎', view: '👁', evidence_view: '👁',
  break_glass: '🔓', blocked: '🚫', dsar: '📤',
};

// One value, short enough to sit in a line of history.
function auditValue(v) {
  if (v === null || v === undefined || v === '') return 'empty';
  if (Array.isArray(v)) return v.length ? v.join(', ') : 'none';
  if (typeof v === 'object') return Object.keys(v).map(function (k) { return k + ': ' + auditValue(v[k]); }).join(', ');
  return String(v);
}

function auditLine(e) {
  var fields = e.after && typeof e.after === 'object' && !Array.isArray(e.after) ? Object.keys(e.after) : [];
  if (e.action === 'update' || e.action === 'access') {
    return fields.map(function (k) {
      var was = e.before ? auditValue(e.before[k]) : 'empty';
      return '<b>' + esc(k) + '</b>: <span translate="no">' + esc(was) + ' → ' + esc(auditValue(e.after[k])) + '</span>';
    }).join('<br>') || 'changed';
  }
  if (e.action === 'open') return 'Case opened';
  if (e.action === 'note') return 'Note added';
  if (e.action === 'advice') return 'Asked JACK for guidance';
  if (e.action === 'letter') return 'Letter drafted (' + esc(auditValue(e.after && e.after.letter_kind)) + ')';
  if (e.action === 'evidence') return 'Evidence added: <span translate="no">' + esc(auditValue(e.after && e.after.name)) + '</span>';
  if (e.action === 'dsar') return 'Copy of the file served to <span translate="no">' + esc(auditValue(e.detail && e.detail.served_to)) + '</span>';
  if (e.action === 'break_glass') return 'Opened a case naming them, reason recorded';
  if (e.action === 'blocked') return 'Refused: <span translate="no">' + esc(auditValue(e.detail && e.detail.rule)) + '</span>';
  if (e.action === 'view') return 'Opened the case file';
  if (e.action === 'evidence_view') return 'Downloaded evidence: <span translate="no">' + esc(auditValue(e.detail && e.detail.name)) + '</span>';
  return esc(e.action);
}

function auditHtml() {
  if (_audit === null) return '<div style="color:var(--sub);font-size:12.5px;padding:8px 0">Loading the history…</div>';
  if (_audit === false) {
    return '<div style="background:var(--ap-orange-bg);border:1px solid var(--ap-orange-line);border-radius:8px;padding:8px 12px;font-size:12.5px;color:var(--ap-orange-deep)">' +
      'The audit trail is not switched on yet. Run <b>migrations/v213-0-dg-audit.sql</b>. Until then changes to this case are saved but not recorded here.</div>';
  }
  if (!_audit.length) return '<div style="color:var(--sub);font-size:12.5px;padding:8px 0">Nothing recorded yet. Every change from here on is logged and cannot be edited or deleted.</div>';
  return _audit.map(function (e) {
    var icon = AUDIT_ICON[e.action] || '•';
    var when = e.at_local ? (e.at_local + ' ' + (e.tz === 'Asia/Qatar' ? 'Qatar' : 'Dubai') + ' time') : esc(e.at || '');
    return '<div style="border-inline-start:3px solid var(--border);padding:4px 0;padding-inline-start:10px;margin-bottom:6px">' +
      '<div style="font-size:11px;color:var(--sub)">' + icon + ' <span translate="no">' + esc(e.actor_name || 'unknown') + '</span> · ' + esc(when) + '</div>' +
      '<div style="font-size:12.5px;line-height:1.5">' + auditLine(e) + '</div>' +
    '</div>';
  }).join('');
}

// ── Evidence (v130.4) ─────────────────────────────────────────────────────────
var _evidence = null;      // null = loading; [] = none; false = needs migration
var _evPreview = {};       // id → data_url once viewed (images render inline)

async function loadEvidence(caseId) {
  try {
    var res = await api('/admin/dg/cases/' + caseId + '/evidence');
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && Array.isArray(data.evidence)) _evidence = data.evidence;
    else _evidence = false;   // 503 pre-migration — the panel says so
  } catch (e) { _evidence = false; }
  if (_open && String(_open.id) === String(caseId)) paint();
}

// Compress an image file onto a bounded JPEG data-URL (the staff bug-report
// posture: the Worker caps at 900K chars, so aim under it with headroom).
function compressImage(file) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    var url = URL.createObjectURL(file);
    img.onload = function () {
      URL.revokeObjectURL(url);
      var scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      var canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      var q = 0.85, out = canvas.toDataURL('image/jpeg', q);
      while (out.length > 850000 && q > 0.3) { q -= 0.15; out = canvas.toDataURL('image/jpeg', q); }
      out.length <= 850000 ? resolve(out) : reject(new Error('too-big'));
    };
    img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('bad-image')); };
    img.src = url;
  });
}

function readAsDataUrl(file) {
  return new Promise(function (resolve, reject) {
    var r = new FileReader();
    r.onload = function () { resolve(String(r.result)); };
    r.onerror = function () { reject(new Error('read-fail')); };
    r.readAsDataURL(file);
  });
}

async function addEvidenceFile() {
  var input = document.getElementById('dgEvFile');
  var file = input && input.files && input.files[0];
  if (!file || !_open || _busy) return;
  var note = ((document.getElementById('dgEvNote') || {}).value || '').trim();
  _busy = true; paint();
  try {
    var isImage = /^image\//.test(file.type);
    var dataUrl;
    try {
      dataUrl = isImage ? await compressImage(file) : await readAsDataUrl(file);
    } catch (e) {
      toastSafe('Could not read that file', 'error'); _busy = false; paint(); return;
    }
    if (dataUrl.length > 900000) {
      toastSafe('Too large even after compression (max ~900KB). For video/CCTV, record WHERE it is held instead.', 'error');
      _busy = false; paint(); return;
    }
    var res = await api('/admin/dg/cases/' + _open.id + '/evidence', {
      method: 'POST',
      body: JSON.stringify({ kind: isImage ? 'image' : 'document', name: file.name, mime: file.type || null, data_url: dataUrl, note: note || undefined }),
    });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.evidence) {
      if (data.notes) _open.notes = data.notes;
      var nb = document.getElementById('dgEvNote'); if (nb) nb.value = '';
      toastSafe('Evidence added to the case file', 'success');
      loadEvidence(_open.id);
    } else toastSafe((data && data.error) || 'Evidence NOT saved', 'error');
  } catch (e) { toastSafe('Network error — the evidence was NOT saved', 'error'); }
  _busy = false; paint();
}

async function addEvidencePointer() {
  var where = ((document.getElementById('dgEvPointer') || {}).value || '').trim();
  var label = ((document.getElementById('dgEvPointerName') || {}).value || '').trim();
  if (!where || !_open || _busy) { if (!where) toastSafe('Say where the evidence is held', 'error'); return; }
  _busy = true; paint();
  try {
    var res = await api('/admin/dg/cases/' + _open.id + '/evidence', {
      method: 'POST',
      body: JSON.stringify({ kind: 'pointer', name: label || 'Externally-held evidence', pointer: where }),
    });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.evidence) {
      if (data.notes) _open.notes = data.notes;
      var a = document.getElementById('dgEvPointer'); if (a) a.value = '';
      var b = document.getElementById('dgEvPointerName'); if (b) b.value = '';
      toastSafe('Recorded on the case file', 'success');
      loadEvidence(_open.id);
    } else toastSafe((data && data.error) || 'Not saved', 'error');
  } catch (e) { toastSafe('Network error — not saved', 'error'); }
  _busy = false; paint();
}

async function viewEvidence(id) {
  if (_evPreview[id]) { _evPreview[id] = null; paint(); return; }   // toggle off
  try {
    var res = await api('/admin/dg/evidence/' + id);
    var data = await res.json().catch(function () { return null; });
    if (!res.ok || !data || !data.evidence) { toastSafe('Could not load the file', 'error'); return; }
    var ev = data.evidence;
    if (ev.data_url && /^data:image\//.test(ev.data_url)) {
      _evPreview[id] = ev.data_url; paint();
    } else if (ev.data_url) {
      // documents download for viewing in their real app — nothing is altered
      var m = ev.data_url.match(/^data:([\w.+-]+\/[\w.+-]+);base64,(.*)$/);
      if (!m) { toastSafe('Bad file data', 'error'); return; }
      var bytes = atob(m[2]);
      var arr = new Uint8Array(bytes.length);
      for (var i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      var blob = new Blob([arr], { type: m[1] });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = ev.name || 'evidence';
      document.body.appendChild(a);
      a.click();
      setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    }
  } catch (e) { toastSafe('Network error', 'error'); }
}

// v133.86 (overnight sweep, L): the roles, the conflict guard and the history belong to ONE case. Left set,
// a newly opened case showed the last case's for a moment, and "Save roles" in that moment would have saved
// the old case's role accounts onto the new one.
function clearCaseState() { _roles = null; _guard = null; _audit = null; }
function back() { _open = null; _newOpen = false; _evidence = null; _evPreview = {}; clearCaseState(); render(); }

// ── Start investigation ───────────────────────────────────────────────────────
function startNew() {
  _newOpen = true;
  // Paint immediately; repaint once the category list arrives (v130.2).
  paint();
  loadCategories().then(function () { if (_newOpen) paint(); });
}

function staffOptions() {
  var list = (typeof staffData !== 'undefined' && Array.isArray(staffData)) ? staffData : [];
  return list
    .filter(function (s) { return s && s.name; })
    .sort(function (a, b) { return String(a.store || '').localeCompare(String(b.store || '')) || String(a.name).localeCompare(String(b.name)); })
    .map(function (s) {
      return '<option translate="no" value="' + s.id + '" data-name="' + esc(s.name) + '" data-store="' + esc(s.store || '') + '">' +
        esc(s.name) + (s.store ? ' — ' + esc(s.store) : '') + '</option>';
    }).join('');
}

function categoryOptions() {
  var list = Array.isArray(_categories) ? _categories : [];
  return list.map(function (c) {
    return '<option value="' + esc(c.name) + '">' + esc(c.name) + '</option>';
  }).join('');
}

function storeOptions() {
  var list = (typeof staffData !== 'undefined' && Array.isArray(staffData)) ? staffData : [];
  var seen = {};
  return list
    .map(function (s) { return s && s.store; })
    .filter(function (st) { if (!st || seen[st]) return false; seen[st] = true; return true; })
    .sort()
    .map(function (st) { return '<option value="' + esc(st) + '">'; })
    .join('');
}

async function submitNew() {
  if (_busy) return;
  var sel = document.getElementById('dgStaffSel');
  var freeName = (document.getElementById('dgStaffFree') || {}).value || '';
  var conductor = ((document.getElementById('dgConductor') || {}).value || '').trim();
  var kind = ((document.getElementById('dgKind') || {}).value || 'disciplinary');
  var note = ((document.getElementById('dgOpenNote') || {}).value || '').trim();
  var payload = { kind: kind, conductor_name: conductor, opening_note: note || undefined };
  if (freeName.trim()) {
    payload.staff_name = freeName.trim();
  } else if (sel && sel.value) {
    var opt = sel.options[sel.selectedIndex];
    payload.staff_id = parseInt(sel.value, 10);
    payload.staff_name = opt ? opt.getAttribute('data-name') : '';
    payload.staff_store = opt ? opt.getAttribute('data-store') : '';
  }
  // v130.2 — the structured record (all optional; the Worker bounds + nulls)
  var sfv = function (id) { var n = document.getElementById(id); return n && n.value ? n.value : undefined; };
  payload.category       = sfv('dgCategory');
  payload.employee_no    = sfv('dgEmpNo');
  payload.incident_date  = sfv('dgIncDate');
  payload.incident_time  = sfv('dgIncTime');
  payload.incident_store = sfv('dgIncStore');
  payload.reported_date  = sfv('dgRepDate');
  payload.reporter_name  = sfv('dgReporter');
  payload.receiver_name  = sfv('dgReceiver');
  // v130.5 — restriction at intake; the server refuses (503) pre-migration
  // rather than opening an unrestricted case about somebody's manager.
  var hoOnly = document.getElementById('dgHoOnly');
  if (hoOnly && hoOnly.checked) payload.visibility = 'head-office';
  if (!payload.staff_name) { toastSafe('Pick the staff member (or type a name)', 'error'); return; }
  if (!conductor) { toastSafe('Record who is conducting the investigation', 'error'); return; }
  _busy = true;
  try {
    var res = await api('/admin/dg/cases', { method: 'POST', body: JSON.stringify(payload) });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.case) { _newOpen = false; clearCaseState(); _open = data.case; await load(); openCase(data.case.id); }
    else toastSafe((data && data.error) || 'Could not open the case', 'error');
  } catch (e) { toastSafe('Network error — try again', 'error'); }
  _busy = false;
}

function toastSafe(msg, kind) {
  if (typeof showToast === 'function') showToast(msg, kind || 'info');
}

// ── Case actions ──────────────────────────────────────────────────────────────
async function addNote() {
  var box = document.getElementById('dgNoteText');
  var text = box ? String(box.value || '').trim() : '';
  if (!text || !_open || _busy) return;
  _busy = true;
  try {
    var res = await api('/admin/dg/cases/' + _open.id + '/note', { method: 'POST', body: JSON.stringify({ text: text }) });
    var data = await res.json().catch(function () { return null; });
    // clear BEFORE the repaint — the note is on the file now, and paint()
    // carries non-empty boxes across the rebuild (v128.20)
    if (res.ok && data && data.notes) { if (box) box.value = ''; _open.notes = data.notes; paint(); }
    else toastSafe((data && data.error) || 'Note not saved', 'error');
  } catch (e) { toastSafe('Network error — the note was NOT saved', 'error'); }
  _busy = false;
}

async function ask() {
  var box = document.getElementById('dgAskText');
  var q = box ? String(box.value || '').trim() : '';
  if (!q || !_open || _busy) return;
  if (box) box.value = '';   // consumed — don't let paint() carry it (v128.20)
  _busy = true; paint();
  try {
    var res = await api('/admin/dg/cases/' + _open.id + '/advise', { method: 'POST', body: JSON.stringify({ question: q }) });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.notes) { _open.notes = data.notes; }
    else if (data && data.answer) { toastSafe(data.answer, 'info'); }
    else toastSafe((data && data.error) || 'No guidance available', 'error');
  } catch (e) { toastSafe('Network error — try again', 'error'); }
  _busy = false;
  paint();
}

async function letter(kind) {
  if (!_open || _busy) return;
  var points = window.prompt('Anything specific to include in the ' + kind + ' letter? (optional)') || '';
  _busy = true; paint();
  try {
    var res = await api('/admin/dg/cases/' + _open.id + '/letter', { method: 'POST', body: JSON.stringify({ letter_kind: kind, points: points }) });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.letter) {
      download('dg-case-' + _open.id + '-' + kind + '-letter.txt', data.letter);
      openCase(_open.id);   // refresh the trail (the draft was appended server-side)
    } else toastSafe((data && (data.error || data.answer)) || 'No draft produced', 'error');
  } catch (e) { toastSafe('Network error — try again', 'error'); }
  _busy = false;
  paint();
}

// The payroll-CSV download pattern — a Blob the browser saves; nothing emailed.
function download(filename, text) {
  var blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  var a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}

// v132.29: a change that saved but was NOT recorded must say so. Silence is
// the one outcome an audit trail may not have.
function warnIfUnrecorded(data) {
  if (data && data.audit_recorded === false) {
    toastSafe('Saved, but NOT recorded in the history: run migration v213-0', 'error');
  }
}


// v132.35: somebody asked to see their own file and we gave them a copy.
// Record that we did. A subject access right does not exist in UAE law yet,
// but a person can ask whatever the law says, the company will answer, and
// a company that can show WHAT it disclosed and WHEN is in a better
// position than one that cannot.
//
// It records the disclosure. It does not BUILD the copy: what to include,
// and what to redact because it names somebody else, is a judgement.
async function recordDisclosure() {
  if (!_open) return;
  var who = window.prompt("Who was given a copy of this file? (their name)");
  if (!who) return;
  var how = window.prompt("How was it given to them? (e.g. printed and handed over, emailed, read in a meeting)") || "";
  try {
    var res = await api("/admin/dg/cases/" + _open.id + "/dsar", {
      method: "POST", body: JSON.stringify({ served_to: who, how: how }),
    });
    var data = await res.json().catch(function () { return null; });
    if (!res.ok) { toastSafe((data && data.error) || "Could not record it.", "error"); return; }
    if (data && data.notes) _open.notes = data.notes;
    toastSafe("Recorded on the file. It cannot be edited or removed.", "success");
    loadAudit(_open.id);
    paint();
  } catch (e) { toastSafe("Could not record it.", "error"); }
}
async function patchCase(patch) {
  if (!_open || _busy) return false;
  _busy = true;
  var ok = false;
  try {
    var res = await api('/admin/dg/cases/' + _open.id, { method: 'PATCH', body: JSON.stringify(patch) });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.case) { _open = Object.assign({}, _open, data.case); ok = true; warnIfUnrecorded(data); }
    else toastSafe((data && data.error) || 'Update failed', 'error');
  } catch (e) { toastSafe('Network error — try again', 'error'); }
  _busy = false;
  if (ok) loadAudit(_open.id);   // v132.29: the history refreshes with the change
  paint();
  return ok;
}

function setStage(v) { patchCase({ stage: v }); }
function setStatus(v) { patchCase({ status: v }); }

// v130.6 — reopening a closed case records WHY on the file (workflow
// section 18: appeal grounds / new evidence). The server refuses a
// reason-less reopen, so the prompt is the real gate, not decoration.
function reopenCase() {
  var reason = window.prompt('Why is this case being reopened? (e.g. the grounds of appeal — recorded on the file)') || '';
  if (!reason.trim()) { toastSafe('A reopen needs a reason — it goes on the case file', 'error'); return; }
  patchCase({ status: 'open', reopen_reason: reason.trim() });
}
async function saveOutcome() {
  var box = document.getElementById('dgOutcome');
  var ok = await patchCase({ outcome: box ? box.value : '' });
  // v128.20: the save was silent — the ops manager couldn't tell whether it took.
  if (ok) toastSafe('Outcome saved to the case file', 'success');
}

// ── Render ────────────────────────────────────────────────────────────────────
var NOTE_STYLE = {
  note:   { icon: '📝', bg: 'var(--card)' },
  advice: { icon: '🤖', bg: 'var(--info-bg)' },
  letter: { icon: '✉️', bg: 'var(--warn-bg)' },
  stage:  { icon: '🔁', bg: 'var(--ok-bg)' },   // v130.3 — automatic pipeline transitions
  evidence: { icon: '🗄', bg: 'var(--ap-violet-bg)' }, // v130.4 — evidence submissions
};

function trailHtml() {
  var notes = Array.isArray(_open.notes) ? _open.notes : [];
  if (!notes.length) return '<div style="color:var(--sub);font-size:12.5px;padding:8px 0">No notes yet — everything you add, ask or draft lands here.</div>';
  return notes.map(function (n) {
    var s = NOTE_STYLE[n.kind] || NOTE_STYLE.note;
    return '<div style="background:' + s.bg + ';border:1px solid var(--border);border-radius:10px;padding:8px 12px;margin-bottom:6px">' +
      '<div translate="no" style="font-size:11px;color:var(--sub)">' + s.icon + ' ' + esc(n.by || '') + ' · ' + esc(fmtWhen(n.at)) + '</div>' +
      '<div translate="no" style="font-size:13px;white-space:pre-wrap;line-height:1.5">' + esc(n.text || '') + '</div>' +
    '</div>';
  }).join('');
}

// v130.2 — the structured record strip on the case header. Shows whatever is
// recorded; the category is editable in place (PATCH whitelisted server-side).
// On a pre-v199-0 DB these fields are simply absent — nothing renders.
function structuredHtml(c) {
  var bits = [];
  var chip = function (label, val) {
    if (!val) return;
    bits.push('<span style="font-size:11.5px;background:var(--surface-3);border-radius:8px;padding:3px 9px">' + label + ' <b translate="no">' + esc(val) + '</b></span>');
  };
  chip('Employee #', c.employee_no);
  chip('Incident', c.incident_date ? (c.incident_date + (c.incident_time ? ' ' + String(c.incident_time).slice(0, 5) : '')) : null);
  chip('At', c.incident_store);
  chip('Reported', c.reported_date);
  chip('By', c.reporter_name);
  chip('Received by', c.receiver_name);
  var catSel = '';
  if (Array.isArray(_categories) && _categories.length) {
    catSel = '<select onchange="DgCases.setCategory(this.value)" style="font-size:11.5px;padding:3px 6px;border:1px solid var(--border-strong);border-radius:8px">' +
      '<option value="">— category —</option>' +
      _categories.map(function (x) {
        return '<option' + (x.name === c.category ? ' selected' : '') + ' value="' + esc(x.name) + '">' + esc(x.name) + '</option>';
      }).join('') + '</select>';
  } else if (c.category) {
    bits.unshift('<span style="font-size:11.5px;background:var(--ap-indigo-bg);color:var(--ap-indigo-deep);border-radius:8px;padding:3px 9px">🏷 <b>' + esc(c.category) + '</b></span>');
  }
  if (!bits.length && !catSel) return '';
  return '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;align-items:center">' + catSel + bits.join('') + '</div>';
}

function setCategory(v) { patchCase({ category: v || null }); }
function setPipeline(v) { patchCase({ pipeline_status: v || null }); }

// v130.3 — the tracking grid (workflow section 5): owner, investigator,
// responsible person, key dates, next action + due. Saved as one PATCH.
function trackingHtml(c) {
  var f = function (id, label, type, val, ph) {
    return '<label style="font-size:11.5px;font-weight:700">' + label + '<br>' +
      '<input id="' + id + '" type="' + type + '" value="' + esc(val || '') + '"' + (ph ? ' placeholder="' + ph + '"' : '') +
      ' style="width:100%;box-sizing:border-box;margin-top:3px;padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px"></label>';
  };
  var overdue = c.next_action_due && c.status === 'open' && c.next_action_due < todayLocal();
  return '<details style="margin-top:10px"' + (overdue ? ' open' : '') + '><summary style="font-size:12.5px;font-weight:700;cursor:pointer">📋 Assignment &amp; tracking' +
    (c.next_action ? ' — next: <span translate="no">' + esc(c.next_action).slice(0, 60) + '</span>' + (c.next_action_due ? ' <span style="color:' + (overdue ? 'var(--ui-bad)' : 'var(--sub)') + '">(due ' + esc(c.next_action_due) + ')</span>' : '') : '') +
    '</summary>' +
    '<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:8px">' +
      f('dgOwner', 'Case owner', 'text', c.case_owner) +
      f('dgInvestigator', 'Investigator', 'text', c.investigator) +
      f('dgResponsible', 'Currently responsible', 'text', c.responsible) +
      f('dgAssigned', 'Date assigned', 'date', c.assigned_date) +
      f('dgInvStart', 'Investigation started', 'date', c.investigation_start) +
      f('dgTarget', 'Target completion', 'date', c.target_date) +
    '</div>' +
    '<div style="display:grid;grid-template-columns:2fr 1fr auto;gap:8px;margin-top:8px;align-items:end">' +
      f('dgNextAction', 'Next action', 'text', c.next_action, 'e.g. review CCTV with mall security') +
      f('dgNextDue', 'Due', 'date', c.next_action_due) +
      '<button onclick="DgCases.saveTracking()" style="background:var(--brand);color:#fff;border:none;border-radius:8px;padding:8px 14px;font-size:12.5px;font-weight:700;cursor:pointer">Save</button>' +
    '</div>' +
  '</details>';
}

async function saveTracking() {
  var v = function (id) { var n = document.getElementById(id); return n ? (n.value || null) : undefined; };
  var ok = await patchCase({
    case_owner: v('dgOwner'), investigator: v('dgInvestigator'), responsible: v('dgResponsible'),
    assigned_date: v('dgAssigned'), investigation_start: v('dgInvStart'), target_date: v('dgTarget'),
    next_action: v('dgNextAction'), next_action_due: v('dgNextDue'),
  });
  if (ok) toastSafe('Tracking saved to the case file', 'success');
}

function detailHtml() {
  var c = _open;
  return '<a href="#" onclick="DgCases.back();return false" style="display:inline-block;margin-bottom:10px;font-size:13px;font-weight:700;color:var(--brand);text-decoration:none">‹ All cases</a>' +
    '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px;margin-bottom:10px">' +
      '<div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center">' +
        '<div translate="no" style="font-size:16px;font-weight:800">' + (c.kind === 'grievance' ? '🤝' : '⚖️') + ' ' + esc(c.staff_name) + '</div>' +
        (c.staff_store ? '<div translate="no" style="font-size:12px;color:var(--sub)">' + esc(c.staff_store) + '</div>' : '') +
        '<span style="font-size:11px;font-weight:700;border-radius:10px;padding:3px 10px;background:' + (c.status === 'open' ? 'var(--elc-green-soft);color:var(--ok-deep)' : 'var(--surface-3);color:var(--sub)') + '">' + esc(c.status) + '</span>' +
      '</div>' +
      '<div style="font-size:11.5px;color:var(--sub);margin-top:4px">Case #' + c.id + ' · ' + esc(c.kind) + ' · opened ' + esc(fmtWhen(c.opened_at)) + ' by <span translate="no">' + esc(c.opened_by_name || '?') + '</span> · conducted by <b translate="no">' + esc(c.conductor_name) + '</b></div>' +
      structuredHtml(c) +
      accessPanelHtml(c) +
      rolesPanelHtml() +
      trackingHtml(c) +
      '<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;align-items:center;font-size:12.5px">' +
        'Pipeline <select onchange="DgCases.setPipeline(this.value)" style="padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px">' +
          '<option value="">— not set —</option>' +
          PIPELINE.map(function (p) { return '<option value="' + p[0] + '"' + (p[0] === c.pipeline_status ? ' selected' : '') + '>' + p[1] + '</option>'; }).join('') + '</select>' +
        'Stage <select onchange="DgCases.setStage(this.value)" style="padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px">' +
          // v135.2: an explicit value, so the stage saved is never the Arabic shown on the option
          STAGES.map(function (s) { return '<option value="' + s + '"' + (s === c.stage ? ' selected' : '') + '>' + s + '</option>'; }).join('') + '</select>' +
        (c.status === 'open'
          ? '<button onclick="if(confirm(\'Close this case? It stays on file — cases are never deleted.\'))DgCases.setStatus(\'closed\')" style="background:var(--card);border:1px solid var(--ui-bad);color:var(--ui-bad);border-radius:8px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer">Close case</button>'
          : '<button onclick="DgCases.reopenCase()" style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer">Reopen</button>') +
        // v132.35: record that a copy of this file was given to the person
        // it is about. The record is permanent, which is the point of it.
        '<button onclick="DgCases.recordDisclosure()" title="Record that a copy of this file was given to the person it is about. Permanent." style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer">📤 Copy served</button>' +
      '</div>' +
      '<div style="display:flex;gap:6px;margin-top:10px">' +
        '<textarea id="dgOutcome" rows="2" placeholder="Outcome (recorded when decided — the decision is yours and HR\'s)" style="flex:1;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px">' + esc(c.outcome || '') + '</textarea>' +
        '<button onclick="DgCases.saveOutcome()" style="background:var(--brand);color:#fff;border:none;border-radius:8px;padding:0 14px;font-size:12.5px;font-weight:700;cursor:pointer">Save</button>' +
      '</div>' +
    '</div>' +

    '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:12px 16px;margin-bottom:10px">' +
      '<div style="font-size:13px;font-weight:700;margin-bottom:6px">🤖 Ask JACK (process guidance — advice only, never a decision)</div>' +
      '<div style="display:flex;gap:6px">' +
        '<input id="dgAskText" type="text" maxlength="600" placeholder="e.g. What should the invite letter include at this stage?" onkeydown="if(event.key===\'Enter\')DgCases.ask()" style="flex:1;min-width:0;padding:9px 11px;border:1px solid var(--border-strong);border-radius:9px;font-size:13px">' +
        '<button onclick="DgCases.ask()"' + (_busy ? ' disabled' : '') + ' style="background:#1d4ed8;color:#fff;border:none;border-radius:9px;padding:9px 14px;font-size:13px;font-weight:700;cursor:pointer">' + (_busy ? '…' : 'Ask') + '</button>' +
      '</div>' +
      '<div style="display:flex;gap:8px;margin-top:8px">' +
        '<button onclick="DgCases.letter(\'invite\')" style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:7px 12px;font-size:12px;font-weight:700;cursor:pointer">✉️ Draft invite-to-hearing letter</button>' +
        '<button onclick="DgCases.letter(\'outcome\')" style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:7px 12px;font-size:12px;font-weight:700;cursor:pointer">✉️ Draft outcome letter</button>' +
        '<button onclick="DgCases.letter(\'appeal\')" style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:7px 12px;font-size:12px;font-weight:700;cursor:pointer">✉️ Draft appeal-outcome letter</button>' +
      '</div>' +
      '<div style="font-size:10.5px;color:var(--sub);margin-top:6px">Letters download as files for HR to review and sign in real life. Every exchange is saved onto the case file.</div>' +
      // v136.9 (Matt, 17 Oct 2026): said where it is used. True of the code: dg-ai.js calls
      // Anthropic directly (not the Ask Jack app) and stores nothing; the route saves the exchange.
      '<div id="dgAiNotice" style="font-size:10.5px;color:var(--sub);margin-top:4px">AI help: the case details you send are processed by Anthropic (our AI provider) to draft advice or a letter. They are not used to train AI and are not kept by the Ask Jack assistant. Your question and the answer are saved on this case file.</div>' +
    '</div>' +

    evidencePanelHtml() +

    '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:12px 16px">' +
      '<div style="font-size:13px;font-weight:700;margin-bottom:8px">📁 Case file</div>' +
      trailHtml() +
      '<div style="display:flex;gap:6px;margin-top:8px">' +
        '<textarea id="dgNoteText" rows="2" placeholder="Add a note to the file…" style="flex:1;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px"></textarea>' +
        '<button onclick="DgCases.addNote()" style="background:var(--brand);color:#fff;border:none;border-radius:8px;padding:0 14px;font-size:12.5px;font-weight:700;cursor:pointer">Add</button>' +
      '</div>' +
    '</div>' +
    // v132.29: the append-only history, on the case, where a challenge is answered.
    '<details style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:12px 16px;margin-top:12px"' + (_audit === false ? ' open' : '') + '>' +
      '<summary style="cursor:pointer;font-size:13px;font-weight:700">🧾 History' + (Array.isArray(_audit) && _audit.length ? ' (' + _audit.length + ')' : '') + '</summary>' +
      '<div style="font-size:11.5px;color:var(--sub);margin:6px 0 10px">Every change, who made it, and what it replaced. Append-only: nobody can edit or delete an entry, including Head Office.</div>' +
      auditHtml() +
    '</details>';
}

// v130.5 — the access panel (workflow sections 2 + 10). Client renders it for
// Head Office or the opener; the SERVER is the enforcement (403/404) — this
// is presentation, not the wall.
var _accounts = null;

function loadAccounts() {
  if (_accounts) return Promise.resolve(_accounts);
  if (typeof DB === 'undefined' || !DB.accounts) { _accounts = []; return Promise.resolve(_accounts); }
  return DB.accounts.list().then(function (rows) {
    _accounts = (rows || []).filter(function (a) { return a && a.id != null; });
    return _accounts;
  }).catch(function () { _accounts = []; return _accounts; });
}

function canEditAccessClient(c) {
  var me = (typeof currentAdmin !== 'undefined' && currentAdmin) ? currentAdmin : null;
  if (!me) return false;
  if (String(me.role || '').toLowerCase() === 'admin') return true;
  return c.opened_by_account_id != null && String(c.opened_by_account_id) === String(me.id);
}

function accessPanelHtml(c) {
  if (!canEditAccessClient(c)) {
    return c.visibility === 'head-office'
      ? '<div style="font-size:11.5px;color:var(--sub);margin-top:8px">🔒 Head Office only</div>' : '';
  }
  var excluded = Array.isArray(c.excluded_account_ids) ? c.excluded_account_ids.map(String) : [];
  var accountBoxes = (_accounts || []).map(function (a) {
    var checked = excluded.indexOf(String(a.id)) !== -1;
    return '<label style="font-size:11.5px;display:flex;align-items:center;gap:5px;cursor:pointer">' +
      '<input type="checkbox" class="dgExcl" value="' + a.id + '"' + (checked ? ' checked' : '') + '>' +
      '<span translate="no">' + esc(a.name || ('account #' + a.id)) + '</span>' + (a.role ? ' <span style="color:var(--sub)">(' + esc(a.role) + ')</span>' : '') +
    '</label>';
  }).join('');
  return '<details style="margin-top:8px"' + (c.visibility === 'head-office' || excluded.length ? ' open' : '') + '>' +
    '<summary style="font-size:12.5px;font-weight:700;cursor:pointer">🔒 Who can see this case' +
      (c.visibility === 'head-office' ? ' — Head Office only' : '') +
      (excluded.length ? ' · ' + excluded.length + ' excluded' : '') + '</summary>' +
    '<div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-top:8px;font-size:12px">' +
      'Visibility <select id="dgVisibility" style="padding:5px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12px">' +
        '<option value="managers"' + (c.visibility !== 'head-office' ? ' selected' : '') + '>All managers</option>' +
        '<option value="head-office"' + (c.visibility === 'head-office' ? ' selected' : '') + '>Head Office only</option>' +
      '</select>' +
      '<button onclick="DgCases.saveAccess()" style="background:var(--brand);color:#fff;border:none;border-radius:8px;padding:6px 12px;font-size:12px;font-weight:700;cursor:pointer">Save access</button>' +
    '</div>' +
    (accountBoxes
      ? '<div style="margin-top:8px"><div style="font-size:11.5px;font-weight:700;margin-bottom:4px">Excluded accounts (the subject of the case, or anyone conflicted — they see nothing, whatever their role):</div>' +
        '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:4px">' + accountBoxes + '</div></div>'
      : '<div style="font-size:11.5px;color:var(--sub);margin-top:6px">Account list unavailable — visibility still saves.</div>') +
  '</details>';
}

async function saveAccess() {
  if (!_open || _busy) return;
  var vis = (document.getElementById('dgVisibility') || {}).value || 'managers';
  var ids = [].slice.call(document.querySelectorAll('.dgExcl:checked')).map(function (n) { return parseInt(n.value, 10); });
  var ok = await patchCase({ visibility: vis, excluded_account_ids: ids });
  if (ok) toastSafe('Access saved — excluded accounts now see nothing of this case', 'success');
}

// v130.4 — the evidence panel (workflow section 11). Submitted records are
// APPEND-ONLY: there is no edit and no delete, and the panel says so.
var EV_ICON = { image: '🖼', document: '📄', statement: '📝', pointer: '📼', other: '📎' };

function evidencePanelHtml() {
  var body;
  if (_evidence === null) {
    body = '<div style="color:var(--sub);font-size:12.5px">Loading…</div>';
  } else if (_evidence === false) {
    body = '<div style="padding:10px;color:var(--warn-deep);font-size:12.5px;background:var(--warn-bg);border:1px solid var(--ap-amber-line);border-radius:8px">🛠 Evidence storage needs <b>migrations/v199-2-dg-evidence.sql</b> — run it in Supabase, then reload.</div>';
  } else if (!_evidence.length) {
    body = '<div style="color:var(--sub);font-size:12.5px;padding:4px 0">No evidence on file yet.</div>';
  } else {
    body = _evidence.map(function (ev) {
      var icon = EV_ICON[ev.kind] || EV_ICON.other;
      return '<div style="border:1px solid var(--border);border-radius:10px;padding:8px 12px;margin-bottom:6px">' +
        '<div style="display:flex;align-items:center;gap:8px">' +
          '<span style="font-size:16px">' + icon + '</span>' +
          '<div style="min-width:0;flex:1">' +
            '<div translate="no" style="font-size:12.5px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + esc(ev.name) + '</div>' +
            '<div style="font-size:11px;color:var(--sub)">' + esc(ev.kind) + ' · <span translate="no">' + esc(ev.uploaded_by) + ' · ' + esc(fmtWhen(ev.uploaded_at)) +
              (ev.note ? ' · ' + esc(ev.note) : '') + '</span></div>' +
            (ev.pointer ? '<div style="font-size:11.5px;margin-top:2px">📍 Held at: <b translate="no">' + esc(ev.pointer) + '</b></div>' : '') +
          '</div>' +
          (ev.kind !== 'pointer'
            ? '<button onclick="DgCases.viewEvidence(' + ev.id + ')" style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:5px 10px;font-size:11.5px;font-weight:700;cursor:pointer">' + (_evPreview[ev.id] ? 'Hide' : 'View') + '</button>'
            : '') +
        '</div>' +
        // v132.89 (overnight audit): the Worker only checks the data: prefix, so a
        // quote after it broke out of src. Only a clean base64 image renders.
        (_evPreview[ev.id] && /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+\/=]+$/.test(_evPreview[ev.id]) ? '<img src="' + _evPreview[ev.id] + '" style="max-width:100%;border-radius:8px;margin-top:8px">' : '') +
      '</div>';
    }).join('');
  }
  var controls = (_evidence === false) ? '' :
    '<div style="display:grid;gap:6px;margin-top:8px">' +
      '<div style="display:flex;gap:6px;align-items:center">' +
        '<input id="dgEvFile" type="file" accept="image/*,.pdf,.doc,.docx,.txt,.eml,.msg" style="flex:1;font-size:12px">' +
        '<input id="dgEvNote" type="text" maxlength="200" placeholder="context (optional)" style="width:180px;padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12px">' +
        '<button onclick="DgCases.addEvidenceFile()"' + (_busy ? ' disabled' : '') + ' style="background:var(--brand);color:#fff;border:none;border-radius:8px;padding:7px 12px;font-size:12px;font-weight:700;cursor:pointer">Attach</button>' +
      '</div>' +
      '<div style="display:flex;gap:6px;align-items:center">' +
        '<input id="dgEvPointerName" type="text" maxlength="120" placeholder="e.g. CCTV 28 Jul 22:00-23:00" style="width:220px;padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12px">' +
        '<input id="dgEvPointer" type="text" maxlength="500" placeholder="…where it is held (mall security, HR drive)" style="flex:1;padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12px">' +
        '<button onclick="DgCases.addEvidencePointer()"' + (_busy ? ' disabled' : '') + ' style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:7px 12px;font-size:12px;font-weight:700;cursor:pointer">Record</button>' +
      '</div>' +
    '</div>';
  return '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:12px 16px;margin-bottom:10px">' +
    '<div style="font-size:13px;font-weight:700;margin-bottom:2px">🗄 Evidence</div>' +
    '<div style="font-size:10.5px;color:var(--sub);margin-bottom:8px">Submitted evidence cannot be edited or deleted — corrections are a new record. Files up to ~900KB; for video/CCTV record where it is held.</div>' +
    body + controls +
  '</div>';
}

function newFormHtml() {
  var acctName = (typeof currentAdmin !== 'undefined' && currentAdmin && currentAdmin.name) ? currentAdmin.name : '';
  return '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px;margin-bottom:12px">' +
    '<div style="font-size:14px;font-weight:800;margin-bottom:10px">🧭 Start an investigation</div>' +
    '<div style="display:grid;gap:8px;max-width:520px">' +
      '<label style="font-size:12px;font-weight:700">Who is it about?<br>' +
        '<select id="dgStaffSel" style="width:100%;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"><option value="">— pick from the directory —</option>' + staffOptions() + '</select></label>' +
      '<input id="dgStaffFree" type="text" placeholder="…or type a name (someone who has left, etc.)" style="padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px">' +
      '<label style="font-size:12px;font-weight:700">Type<br>' +
        '<select id="dgKind" style="width:100%;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"><option value="disciplinary">⚖️ Disciplinary</option><option value="grievance">🤝 Grievance</option></select></label>' +
      '<label style="font-size:12px;font-weight:700">Who is conducting it? (recorded on the case)<br>' +
        '<input id="dgConductor" type="text" value="' + esc(acctName) + '" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
      // v130.2 — the structured record (workflow sections 3–4). All optional:
      // a quick case still opens with just a name + conductor.
      '<label style="font-size:12px;font-weight:700">Category<br>' +
        '<select id="dgCategory" style="width:100%;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"><option value="">— pick a category —</option>' + categoryOptions() + '</select></label>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">' +
        '<label style="font-size:12px;font-weight:700">Employee number<br><input id="dgEmpNo" type="text" maxlength="40" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
        '<label style="font-size:12px;font-weight:700">Store where it happened<br><input id="dgIncStore" type="text" list="dgStoreList" placeholder="if different from current store" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
        '<label style="font-size:12px;font-weight:700">Date of incident<br><input id="dgIncDate" type="date" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
        '<label style="font-size:12px;font-weight:700">Time of incident<br><input id="dgIncTime" type="time" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
        '<label style="font-size:12px;font-weight:700">Date reported<br><input id="dgRepDate" type="date" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
        '<label style="font-size:12px;font-weight:700">Reported by<br><input id="dgReporter" type="text" maxlength="120" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
        '<label style="font-size:12px;font-weight:700">Received by<br><input id="dgReceiver" type="text" maxlength="120" style="width:100%;box-sizing:border-box;margin-top:4px;padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></label>' +
      '</div>' +
      '<datalist id="dgStoreList">' + storeOptions() + '</datalist>' +
      '<textarea id="dgOpenNote" rows="3" placeholder="What happened? (the opening note on the file — optional)" style="padding:8px 10px;border:1px solid var(--border-strong);border-radius:8px;font-size:13px"></textarea>' +
      // v130.5 — restriction at intake (the against-a-manager routes)
      '<label style="font-size:12px;display:flex;align-items:center;gap:6px;cursor:pointer">' +
        '<input id="dgHoOnly" type="checkbox" style="width:16px;height:16px">' +
        '🔒 Sensitive: Head Office only — tick when the case concerns a manager, or anyone who must not see it</label>' +
      '<div style="display:flex;gap:8px">' +
        '<button onclick="DgCases.submitNew()"' + (_busy ? ' disabled' : '') + ' style="background:var(--brand);color:#fff;border:none;border-radius:9px;padding:10px 16px;font-size:13px;font-weight:700;cursor:pointer">Open the case</button>' +
        '<button onclick="DgCases.back()" style="background:var(--card);border:1px solid var(--border-strong);border-radius:9px;padding:10px 16px;font-size:13px;cursor:pointer">Cancel</button>' +
      '</div>' +
    '</div>' +
  '</div>';
}

function listHtml() {
  if (_error) {
    var needsMigration = /v177/.test(_error);
    return '<div style="padding:16px;color:var(--warn-deep);font-size:13px;background:var(--warn-bg);border:1px solid var(--ap-amber-line);border-radius:10px">' +
      (needsMigration ? '🛠 The D&amp;G module is built but its table is missing — run <b>migrations/v177-0-dg-cases.sql</b> in Supabase, then reload.' : '⚠ ' + esc(_error)) +
      '</div>';
  }
  if (!_cases.length) return '<div style="padding:20px;text-align:center;color:var(--sub);font-size:13px">No cases on file. Opening one records who conducted it, every note, and every letter — even a case with nothing to answer documents the process.</div>';
  return _cases.map(function (c) {
    return '<div onclick="DgCases.openCase(' + c.id + ')" style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:12px 14px;margin-bottom:8px;cursor:pointer;display:flex;align-items:center;gap:10px">' +
      '<div style="font-size:18px">' + (c.kind === 'grievance' ? '🤝' : '⚖️') + '</div>' +
      '<div style="min-width:0;flex:1">' +
        '<div translate="no" style="font-weight:700;font-size:13.5px">' + esc(c.staff_name) + (c.staff_store ? ' <span style="font-weight:400;color:var(--sub)">· ' + esc(c.staff_store) + '</span>' : '') + '</div>' +
        // v135.2: each label in its own span and each name or date in a translate="no" one, so the labels translate
        '<div style="font-size:11.5px;color:var(--sub)">#' + c.id + ' · <span>' + esc(c.kind) + '</span>' + (c.category ? ' · 🏷 <span>' + esc(c.category) + '</span>' : '') + ' · <span>' + esc(c.pipeline_status ? pipelineLabel(c.pipeline_status) : c.stage) + '</span> · <span>conducted by</span> <span translate="no">' + esc(c.conductor_name) + '</span> · <span>updated</span> <span translate="no">' + esc(fmtWhen(c.updated_at)) + '</span>' +
          (c.next_action_due && c.status === 'open' && c.next_action_due < todayLocal() ? ' · <span style="color:var(--ui-bad);font-weight:700">⏰ action overdue</span>' : '') + '</div>' +
      '</div>' +
      '<span style="font-size:11px;font-weight:700;border-radius:10px;padding:3px 10px;background:' + (c.status === 'open' ? 'var(--elc-green-soft);color:var(--ok-deep)' : 'var(--surface-3);color:var(--sub)') + '">' + esc(c.status) + '</span>' +
    '</div>';
  }).join('');
}

// v128.20 (the ops manager's pilot, 30 Jul): every action repaints the WHOLE panel,
// and half-typed text in the OTHER boxes was destroyed — type an outcome,
// add a note (or change stage / ask JACK), and the unsaved outcome text was
// gone, unretrievable. paint() now carries in-progress values across the
// rebuild. Boxes whose content was just CONSUMED (a sent note/question) are
// cleared by their actions BEFORE the repaint, so sent text never reappears.
var PRESERVE_IDS = ['dgOutcome', 'dgNoteText', 'dgAskText', 'dgOpenNote', 'dgStaffFree', 'dgConductor',
  // v130.2 — the structured-record form fields survive repaints too
  'dgCategory', 'dgEmpNo', 'dgIncDate', 'dgIncTime', 'dgIncStore', 'dgRepDate', 'dgReporter', 'dgReceiver',
  // v130.3 — the tracking grid
  'dgOwner', 'dgInvestigator', 'dgResponsible', 'dgAssigned', 'dgInvStart', 'dgTarget', 'dgNextAction', 'dgNextDue',
  // v130.4 — evidence context + pointer fields
  'dgEvNote', 'dgEvPointerName', 'dgEvPointer'];

function paint() {
  var el = root();
  if (!el) return;
  var keep = {};
  PRESERVE_IDS.forEach(function (id) {
    var n = document.getElementById(id);
    if (n && n.value) keep[id] = n.value;
  });
  if (_open) { el.innerHTML = detailHtml(); }
  else {
    el.innerHTML =
      '<div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">' +
        '<div><div style="font-size:16px;font-weight:800">🧭 Disciplinary &amp; grievance</div>' +
        '<div style="font-size:11.5px;color:var(--sub)">Case files are permanent · JACK advises, people decide · letters need real HR sign-off · notes you send to JACK are processed by Anthropic</div></div>' +
        '<button onclick="DgCases.startNew()" style="margin-inline-start:auto;background:var(--brand);color:#fff;border:none;border-radius:9px;padding:9px 14px;font-size:13px;font-weight:700;cursor:pointer">🧭 Start investigation</button>' +
      '</div>' +
      (_newOpen ? newFormHtml() : '') +
      listHtml();
  }
  // Restore — the typist's in-progress text wins over any prefill (the
  // saved-outcome prefill equals the kept value right after a save anyway).
  Object.keys(keep).forEach(function (id) {
    var n = document.getElementById(id);
    if (n) n.value = keep[id];
  });
}

function render() {
  _open = null;
  paint();
  load();
}

window.DgCases = {
  render: render,
  openCase: openCase,
  back: back,
  startNew: startNew,
  submitNew: submitNew,
  addNote: addNote,
  ask: ask,
  letter: letter,
  setStage: setStage,
  setStatus: setStatus,
  saveOutcome: saveOutcome,
  setCategory: setCategory,
  setPipeline: setPipeline,
  saveTracking: saveTracking,
  addEvidenceFile: addEvidenceFile,
  addEvidencePointer: addEvidencePointer,
  viewEvidence: viewEvidence,
  saveAccess: saveAccess,
  reopenCase: reopenCase,
  saveRoles: saveRoles,
  recordDisclosure: recordDisclosure,
};

export { render, openCase, back, startNew, submitNew, addNote, ask, letter, setStage, setStatus, saveOutcome, setCategory, setPipeline, saveTracking, addEvidenceFile, addEvidencePointer, viewEvidence, saveAccess, reopenCase, saveRoles, recordDisclosure };
